/**
 * KISSED University · multi-section admissions application (UAT-revised).
 * Route: /university/apply
 *
 * Submit flow:
 *   POST /applications           → status=payment_pending
 *   navigate(/university/application-submitted?…)
 *   On that page, applicant clicks "Pay via Square" which calls
 *   POST /applications/:id/checkout and opens the Square link in a new tab.
 *   Admin reconciles manually once Square confirms the transaction.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CreditCard, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { readQuizState, CE_COURSES } from "./quiz/quizData";

function ChipGroup({ options, value, onChange, testidPrefix }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-group`}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o} type="button"
            data-testid={`${testidPrefix}-${o.replace(/\W+/g, "-").toLowerCase()}`}
            onClick={() => toggle(o)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              on ? "bg-purple-900 text-white border-purple-900" : "bg-white text-slate-700 border-slate-200 hover:border-purple-400"
            }`}
          >{o}</button>
        );
      })}
    </div>
  );
}

const SECTIONS = ["Identity", "Education", "Professional", "Motivation", "Program & Readiness"];

const QUIZ_START_MONTHS = ["June 2026", "July 2026", "August 2026", "September 2026", "I need admissions guidance"];

export default function UniversityApply() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Quiz context (query params take precedence over sessionStorage).
  const quizState = useMemo(() => readQuizState(), []);
  const quizContext = useMemo(() => {
    const pathwaySource = (searchParams.get("pathway_source") || "").trim();
    if (!pathwaySource && !quizState) return null;
    return {
      pathway_source: pathwaySource || (quizState ? "admissions_quiz" : "direct_apply"),
      quiz_key: searchParams.get("quiz_key") || quizState?.recommendation || "",
      primary_program: searchParams.get("primary_program") || "",
      quiz_recommended_program: searchParams.get("quiz_recommended_program") || "",
      desired_start_month: searchParams.get("desired_start_month") || quizState?.desired_start_month || "",
      ce_course_interest: searchParams.get("ce_course_interest") || "",
      quiz_answers: quizState?.answers || null,
    };
  }, [searchParams, quizState]);

  const [form, setForm] = useState({
    website: "", // honeypot — must stay empty
    full_name: "", email: "", phone: "", country: "", state_province: "", city: "",
    highest_education_level: "", credentials_held: [], license_number: "", prior_kissed_alumni: "",
    current_professional_status: "",
    years_in_practice: "", current_modalities: [], practice_setting: "",
    portfolio_url: "", employment_status: "",
    why_kissed: "", learning_goals: "", alignment_statement: "",
    primary_program: "", secondary_program: "", specialization_track: "",
    enrollment_preference: "",
    attendance_mode: "", start_window: "", hours_per_week_available: "", heard_from: "",
    payment_readiness_acknowledged: false, truthfulness_acknowledged: false,
    // Admissions Quiz integration
    desired_start_month: "",
    ce_course_interest: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    universityApi.options().then((r) => setOpts(r.data)).catch(() => setOpts(null));
  }, []);

  // Hydrate from quiz context once the catalog is loaded so we can validate
  // the suggested primary_program against live options.
  useEffect(() => {
    if (!opts || !quizContext) return;
    setForm((f) => {
      const next = { ...f };
      const allOptions = opts.program_options || [];
      const candidate = (quizContext.primary_program || "").trim();
      if (!f.primary_program && candidate && allOptions.includes(candidate)) {
        next.primary_program = candidate;
      }
      if (!f.desired_start_month && quizContext.desired_start_month) {
        next.desired_start_month = quizContext.desired_start_month;
      }
      if (!f.ce_course_interest && quizContext.ce_course_interest) {
        next.ce_course_interest = quizContext.ce_course_interest;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  const tracksForPrimary = useMemo(() => {
    if (!opts?.specialization_tracks || !form.primary_program) return [];
    // The catalog tells us which division a program belongs to (option strings
    // are no longer "<division> · <title>" since iter-13).
    const divisionForOption = (option) => {
      for (const group of (opts.program_options_grouped || [])) {
        if (group.options.includes(option)) return group.division;
      }
      return "";
    };
    const div = divisionForOption(form.primary_program);
    return opts.specialization_tracks.filter((t) => t.division === div);
  }, [opts, form.primary_program]);

  // If primary program changes to a division without the current specialization track, clear it.
  useEffect(() => {
    if (!form.specialization_track) return;
    if (!tracksForPrimary.some((t) => t.label === form.specialization_track)) {
      set("specialization_track", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.primary_program]);

  const canAdvance = useMemo(() => {
    if (step === 0) return form.full_name.length > 1 && /^\S+@\S+\.\S+$/.test(form.email) && form.phone.replace(/\D/g, "").length >= 7;
    if (step === 2) return !!form.current_professional_status;
    if (step === 3) return form.why_kissed.length >= 20 && form.learning_goals.length >= 20;
    if (step === 4) {
      return form.primary_program &&
        (!form.secondary_program || form.secondary_program !== form.primary_program) &&
        form.enrollment_preference &&
        form.heard_from &&
        form.payment_readiness_acknowledged &&
        form.truthfulness_acknowledged;
    }
    return true;
  }, [step, form]);

  const submitAndCheckout = async () => {
    if (!opts?.square_configured) {
      toast.error("Payment processor is not configured. Please contact admissions.");
      return;
    }
    setSubmitting(true);
    try {
      // Persist the application (status=payment_pending, fee_status=Pending).
      const payload = {
        ...form,
        secondary_program: form.secondary_program || "",
        specialization_track: form.specialization_track || "",
        // Admissions Quiz integration — additive, optional.
        pathway_source: quizContext?.pathway_source || "direct_apply",
        quiz_recommended_program: quizContext?.quiz_recommended_program || "",
        selected_program: form.primary_program,
        desired_start_month: form.desired_start_month || "",
        ce_course_interest: form.ce_course_interest || "",
        quiz_answers: quizContext?.quiz_answers || null,
      };
      const r = await universityApi.submit(payload);
      const applicationId = r?.data?.application_id;
      const publicApplicationId = r?.data?.public_application_id || "";
      if (!applicationId) throw new Error("No application id returned");

      // Remember identifiers so the Thank You page (and status page) can
      // resolve the applicant after a refresh.
      try {
        sessionStorage.setItem("kissed_uni_last_email", form.email);
        sessionStorage.setItem("kissed_uni_last_application_id", applicationId);
        if (publicApplicationId) {
          sessionStorage.setItem("kissed_uni_last_public_application_id", publicApplicationId);
        }
      } catch { /* ignore */ }

      // Navigate to the confirmation page. The applicant pays via Square from
      // there (opens in a new tab) so they keep their Application ID visible.
      const qs = new URLSearchParams({
        application_id: applicationId,
        email: form.email,
      });
      if (publicApplicationId) qs.set("public_application_id", publicApplicationId);
      navigate(`/university/application-submitted?${qs.toString()}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) toast.message("Admissions are temporarily closed.");
      else if (status === 429) toast.error("Too many submissions from this network. Please wait a bit and try again.");
      else toast.error(formatApiError(err, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!opts) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
  );
  if (!opts.module_enabled) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div className="max-w-md rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h2 className="font-display text-2xl text-amber-900">Admissions are paused</h2>
        <p className="mt-2 text-sm text-amber-800">Please check back soon, or contact admissions@kissedbycvon.com.</p>
      </div>
    </div>
  );

  const fee = opts.application_fee_usd || 50;
  const reviewWindow = opts.review_window_days || "3–5 business days";

  return (
    <div data-testid="university-apply" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/university" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KISSED University · Admissions</span>
          </Link>
          <span className="text-xs text-slate-500">Step {step + 1} of {SECTIONS.length} — {SECTIONS[step]}</span>
        </div>
        <div className="h-1 bg-slate-100"><div className="h-1 bg-purple-900 transition-all" style={{ width: `${((step+1)/SECTIONS.length)*100}%` }} /></div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        {quizContext && quizContext.pathway_source === "admissions_quiz" && (
          <div
            data-testid="quiz-context-banner"
            className="mb-6 rounded-2xl border border-purple-200 bg-purple-50/70 p-4 flex items-start gap-3"
          >
            <Sparkles className="w-5 h-5 text-purple-900 shrink-0 mt-0.5" />
            <div className="text-sm text-purple-900 leading-relaxed">
              <div className="font-medium">From your Pathway Quiz</div>
              {quizContext.quiz_recommended_program ? (
                <div className="mt-0.5">
                  Recommended program: <strong data-testid="banner-quiz-program">{quizContext.quiz_recommended_program}</strong>
                </div>
              ) : null}
              {quizContext.desired_start_month ? (
                <div className="mt-0.5">
                  Desired start: <strong data-testid="banner-quiz-start">{quizContext.desired_start_month}</strong>
                </div>
              ) : null}
              {quizContext.ce_course_interest ? (
                <div className="mt-0.5">
                  CE interest: <strong data-testid="banner-ce-interest">{quizContext.ce_course_interest}</strong>
                </div>
              ) : null}
              <div className="mt-1 text-xs">
                Your selection below confirms the recommended pathway — you can change it before submitting.
              </div>
            </div>
          </div>
        )}

        {/* Honeypot */}
        <input type="text" name="website" autoComplete="off" tabIndex={-1}
          value={form.website} onChange={(e) => set("website", e.target.value)}
          style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} aria-hidden="true" />

        {step === 0 && (
          <section className="space-y-5" data-testid="apply-section-identity">
            <h2 className="font-display text-2xl text-slate-900">Tell us who you are</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full name *</Label><Input data-testid="apply-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2" /></div>
              <div><Label>Email *</Label><Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2" /></div>
              <div><Label>Phone * <span className="text-xs text-slate-500">(international format)</span></Label><Input data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2" placeholder="+1 (555) 123-4567" /></div>
              <div><Label>Country</Label><Input data-testid="apply-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-2" /></div>
              <div><Label>State / Province</Label><Input data-testid="apply-state" value={form.state_province} onChange={(e) => set("state_province", e.target.value)} className="mt-2" /></div>
              <div><Label>City</Label><Input data-testid="apply-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-2" /></div>
            </div>
            <p className="text-xs text-slate-500">We frequently contact applicants by phone or text regarding admissions decisions.</p>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-5" data-testid="apply-section-education">
            <h2 className="font-display text-2xl text-slate-900">Education background</h2>
            <div>
              <Label>Highest education completed</Label>
              <Select value={form.highest_education_level} onValueChange={(v) => set("highest_education_level", v)}>
                <SelectTrigger className="mt-2" data-testid="apply-education-level"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{(opts.education_levels || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Credentials held (multi-select)</Label>
              <div className="mt-2"><ChipGroup options={opts.credentials || []} value={form.credentials_held} onChange={(v) => set("credentials_held", v)} testidPrefix="apply-cred" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>License number (if applicable)</Label><Input data-testid="apply-license" value={form.license_number} onChange={(e) => set("license_number", e.target.value)} className="mt-2" /></div>
              <div>
                <Label>Have you taken a KISSED course before?</Label>
                <Select value={form.prior_kissed_alumni} onValueChange={(v) => set("prior_kissed_alumni", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-alumni"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="space-y-5" data-testid="apply-section-professional">
            <h2 className="font-display text-2xl text-slate-900">Professional background</h2>
            <div>
              <Label>Current Professional Status *</Label>
              <Select value={form.current_professional_status} onValueChange={(v) => set("current_professional_status", v)}>
                <SelectTrigger className="mt-2" data-testid="apply-professional-status"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{(opts.professional_status || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Years in practice</Label>
                <Select value={form.years_in_practice} onValueChange={(v) => set("years_in_practice", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-years"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.years_in_practice || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Current employment status</Label>
                <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-employment"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.employment_status || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Practice setting</Label>
                <Select value={form.practice_setting} onValueChange={(v) => set("practice_setting", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-practice"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.practice_settings || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Portfolio URL (optional)</Label><Input data-testid="apply-portfolio" value={form.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} className="mt-2" placeholder="https://" /></div>
            </div>
            <div>
              <Label>Current modalities you offer (multi-select)</Label>
              <div className="mt-2"><ChipGroup options={["hair care","esthetics","massage","nutrition","herbalism","energy work","coaching","other"]} value={form.current_modalities} onChange={(v) => set("current_modalities", v)} testidPrefix="apply-modality" /></div>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-5" data-testid="apply-section-motivation">
            <h2 className="font-display text-2xl text-slate-900">Why KISSED?</h2>
            <div><Label>Why KISSED? *<span className="text-xs text-slate-500"> (min 20 chars)</span></Label><Textarea data-testid="apply-why" value={form.why_kissed} onChange={(e) => set("why_kissed", e.target.value)} className="mt-2 min-h-[120px]" /></div>
            <div><Label>What do you hope to learn? *</Label><Textarea data-testid="apply-goals" value={form.learning_goals} onChange={(e) => set("learning_goals", e.target.value)} className="mt-2 min-h-[120px]" /></div>
            <div><Label>How does this work call you? (optional)</Label><Textarea data-testid="apply-alignment" value={form.alignment_statement} onChange={(e) => set("alignment_statement", e.target.value)} className="mt-2 min-h-[96px]" /></div>
          </section>
        )}

        {step === 4 && (
          <section className="space-y-6" data-testid="apply-section-program">
            <h2 className="font-display text-2xl text-slate-900">Program selection &amp; readiness</h2>

            {/* Primary */}
            <div>
              <Label>Primary Program of Interest *</Label>
              <Select value={form.primary_program} onValueChange={(v) => set("primary_program", v)}>
                <SelectTrigger className="mt-2" data-testid="apply-primary-program"><SelectValue placeholder="Choose one…" /></SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {(opts.program_options_grouped || []).map((group) => (
                    <div key={group.division}>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-purple-900 bg-purple-50/50 font-semibold sticky top-0">
                        {group.division}
                      </div>
                      {group.options.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Secondary */}
            <div>
              <Label>Secondary Program of Interest <span className="text-xs text-slate-500">(optional)</span></Label>
              <Select value={form.secondary_program || "__none__"} onValueChange={(v) => set("secondary_program", v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-2" data-testid="apply-secondary-program"><SelectValue placeholder="No secondary program" /></SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  <SelectItem value="__none__">No secondary program</SelectItem>
                  {(opts.program_options_grouped || []).map((group) => (
                    <div key={group.division}>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-purple-900 bg-purple-50/50 font-semibold sticky top-0">
                        {group.division}
                      </div>
                      {group.options
                        .filter((p) => p !== form.primary_program)
                        .map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Specialization Track (only when primary is in a division that has tracks) */}
            {tracksForPrimary.length > 0 && (
              <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4" data-testid="apply-specialization-section">
                <Label>Specialization Track <span className="text-xs text-slate-500">(optional)</span></Label>
                <Select value={form.specialization_track || "__none__"} onValueChange={(v) => set("specialization_track", v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-specialization-track"><SelectValue placeholder="No specialization" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No specialization</SelectItem>
                    {tracksForPrimary.map((t) => <SelectItem key={t.label} value={t.label}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {tracksForPrimary.map((t) => form.specialization_track === t.label ? (
                  <p key={t.label} className="mt-2 text-xs text-slate-700 leading-relaxed">{t.description}</p>
                ) : null)}
              </div>
            )}

            {/* CE Course Interest — visible when the chosen primary program belongs to the CE college */}
            {(() => {
              const ceGroup = (opts.program_options_grouped || []).find(
                (g) => g.division === "College of Professional & Continuing Education"
              );
              const isCE = !!ceGroup && ceGroup.options.includes(form.primary_program);
              if (!isCE) return null;
              return (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="apply-ce-interest-section">
                  <Label>CE Course Interest <span className="text-xs text-slate-500">(optional — helps our team confirm the right course)</span></Label>
                  <Select value={form.ce_course_interest || "__none__"} onValueChange={(v) => set("ce_course_interest", v === "__none__" ? "" : v)}>
                    <SelectTrigger className="mt-2" data-testid="apply-ce-course-interest"><SelectValue placeholder="No preference yet" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No preference yet</SelectItem>
                      {CE_COURSES.map((c) => <SelectItem key={c.title} value={c.title}>{c.title} — {c.hours}</SelectItem>)}
                      <SelectItem value="Not sure yet / Need guidance">Not sure yet — I need guidance</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-2 text-xs text-amber-900 leading-relaxed">
                    CE courses are standalone professional development. Your $50 application fee secures admissions review and our team will follow up with course dates, pricing, and registration instructions.
                  </p>
                </div>
              );
            })()}

            {/* Desired Start Month (Admissions Quiz captures specific months) */}
            <div>
              <Label>Desired Start Month <span className="text-xs text-slate-500">(optional)</span></Label>
              <Select value={form.desired_start_month || "__none__"} onValueChange={(v) => set("desired_start_month", v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-2" data-testid="apply-desired-start-month"><SelectValue placeholder="Select a start month…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No preference</SelectItem>
                  {QUIZ_START_MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Enrollment Preference */}
            <div>
              <Label>Enrollment Preference *</Label>
              <RadioGroup
                className="mt-2 grid sm:grid-cols-3 gap-3"
                value={form.enrollment_preference}
                onValueChange={(v) => set("enrollment_preference", v)}
              >
                {[
                  ["pay_in_full", "Pay Program Tuition in Full"],
                  ["pay_per_course", "Pay Per Course"],
                  ["undecided", "Undecided"],
                ].map(([val, label]) => (
                  <label
                    key={val}
                    className={`rounded-xl border p-3 text-sm cursor-pointer flex items-start gap-2 ${
                      form.enrollment_preference === val ? "border-purple-700 bg-purple-50" : "border-slate-200 bg-white hover:border-purple-300"
                    }`}
                    data-testid={`apply-enrollment-${val}`}
                  >
                    <RadioGroupItem value={val} className="mt-0.5" />
                    <span>{label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Schedule + heard from */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Attendance mode</Label>
                <Select value={form.attendance_mode} onValueChange={(v) => set("attendance_mode", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-attendance"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.attendance_mode || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start window</Label>
                <Select value={form.start_window} onValueChange={(v) => set("start_window", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-start-window"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.start_window || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hours per week available</Label>
                <Select value={form.hours_per_week_available} onValueChange={(v) => set("hours_per_week_available", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-hours"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.hours_per_week || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>How Did You Hear About Us? *</Label>
                <Select value={form.heard_from} onValueChange={(v) => set("heard_from", v)}>
                  <SelectTrigger className="mt-2" data-testid="apply-heard-from"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(opts.heard_from || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Square payment notice */}
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 flex gap-2" data-testid="square-payment-banner">
              <CreditCard className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Payment is processed by <strong>Square</strong>. After you click <em>Continue to payment</em>, you'll be taken to a secure Square page to pay your <strong>non-refundable ${fee} application fee</strong>. Your application is saved before checkout — you can return any time using your Application ID and email.
              </div>
            </div>

            {/* Acknowledgements */}
            <div className="rounded-2xl border border-purple-200 bg-purple-50/60 p-4 space-y-3">
              <label className="flex gap-3 items-start" data-testid="apply-readiness-ack-row">
                <Checkbox data-testid="apply-readiness-ack" checked={form.payment_readiness_acknowledged} onCheckedChange={(v) => set("payment_readiness_acknowledged", !!v)} />
                <span className="text-sm text-slate-800 leading-relaxed">
                  A <strong>non-refundable ${fee} application fee</strong> is required to submit your application for admissions review. Our Admissions Team reviews completed applications within <strong>{reviewWindow}</strong>. If accepted, I will choose either <strong>Pay Tuition in Full</strong> or <strong>Pay Per Course</strong>, and all tuition and course fees must be paid before access to coursework is granted.
                </span>
              </label>
              <label className="flex gap-3 items-start" data-testid="apply-truth-ack-row">
                <Checkbox data-testid="apply-truth-ack" checked={form.truthfulness_acknowledged} onCheckedChange={(v) => set("truthfulness_acknowledged", !!v)} />
                <span className="text-sm text-slate-800 leading-relaxed">
                  I confirm all information provided is truthful and consent to KISSED University reviewing this application.
                </span>
              </label>
            </div>
          </section>
        )}

        <div className="mt-10 flex justify-between">
          <Button variant="outline" disabled={step === 0 || submitting} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="apply-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />Back
          </Button>
          {step < SECTIONS.length - 1 ? (
            <Button disabled={!canAdvance} onClick={() => setStep((s) => Math.min(SECTIONS.length - 1, s + 1))} data-testid="apply-next-btn">
              Continue<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button disabled={!canAdvance || submitting} onClick={submitAndCheckout} data-testid="apply-pay-btn" className="bg-purple-900 hover:bg-purple-800">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
              Continue to Square payment · ${fee}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
